import { onBeforeUnmount, watch } from 'vue';
import Swal from 'sweetalert2';
import { onSessionMessage, sendSessionMessage, useAuthStore } from '@/stores/auth';
import { useApiStore } from '@/stores/api';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_BEFORE_MS = 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;
const ACTIVITY_KEY = 'last_activity_at';

export function useIdleTimeout(): void {
    const authStore = useAuthStore();
    const apiStore = useApiStore();

    let warningTimer: number | null = null;
    let logoutTimer: number | null = null;
    let countdownInterval: number | null = null;
    let unsubscribeSession: (() => void) | null = null;
    let warningOpen = false;
    let warningDismissedExternally = false;
    let started = false;

    const readLastActivity = (): number => {
        const raw = localStorage.getItem(ACTIVITY_KEY);
        const parsed = raw === null ? NaN : Number(raw);
        return Number.isFinite(parsed) ? parsed : Date.now();
    };

    const writeLastActivity = (): void => {
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    };

    const clearTimer = (id: number | null): null => {
        if (id !== null) window.clearTimeout(id);
        return null;
    };

    const stopCountdown = (): void => {
        if (countdownInterval !== null) {
            window.clearInterval(countdownInterval);
            countdownInterval = null;
        }
    };

    const scheduleTimers = (): void => {
        warningTimer = clearTimer(warningTimer);
        logoutTimer = clearTimer(logoutTimer);

        const lastActivity = readLastActivity();
        const now = Date.now();
        const warnAt = lastActivity + IDLE_TIMEOUT_MS - WARNING_BEFORE_MS;
        const logoutAt = lastActivity + IDLE_TIMEOUT_MS;

        if (now >= logoutAt) {
            performLogout();
            return;
        }

        if (now >= warnAt) {
            showWarning();
        } else {
            warningTimer = window.setTimeout(showWarning, warnAt - now);
        }
        logoutTimer = window.setTimeout(performLogout, logoutAt - now);
    };

    // Suppressed while the warning modal is open: the user must explicitly click a button,
    // otherwise a stray cursor near the modal would silently extend the session.
    const onLocalActivity = (): void => {
        if (warningOpen) return;
        writeLastActivity();
        sendSessionMessage({ type: 'activity' });
        scheduleTimers();
    };

    const onForeignActivity = (): void => {
        if (warningOpen) {
            warningDismissedExternally = true;
            Swal.close();
            warningOpen = false;
            stopCountdown();
        }
        scheduleTimers();
    };

    // Originator already called /api/user/logout — just clean up local state.
    const onForeignLogout = (): void => {
        warningDismissedExternally = true;
        stop();
        authStore.localLogout();
    };

    const onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') onLocalActivity();
    };

    const showWarning = async (): Promise<void> => {
        if (warningOpen) return;
        warningOpen = true;
        warningDismissedExternally = false;
        let secondsLeft = Math.floor(WARNING_BEFORE_MS / 1000);

        const result = await Swal.fire({
            title: 'Inactive session',
            html: `You will be logged out in <b>${secondsLeft}</b> seconds due to inactivity.`,
            icon: 'warning',
            showCancelButton: false,
            confirmButtonText: 'Stay signed in',
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                countdownInterval = window.setInterval(() => {
                    secondsLeft -= 1;
                    const counter = Swal.getPopup()?.querySelector('b');
                    if (counter) counter.textContent = String(secondsLeft);
                    if (secondsLeft <= 0) {
                        stopCountdown();
                        Swal.close();
                    }
                }, 1000);
            },
            willClose: stopCountdown,
        });

        warningOpen = false;

        if (warningDismissedExternally) {
            warningDismissedExternally = false;
            return;
        }

        if (!started) return;

        if (result.isConfirmed) {
            try {
                await apiStore.axiosAuth.get('/user');
            } catch {}
            writeLastActivity();
            sendSessionMessage({ type: 'activity' });
            scheduleTimers();
            return;
        }

        performLogout();
    };

    const performLogout = (): void => {
        stop();
        authStore.logoutUser();
    };

    const start = (): void => {
        if (started) return;
        started = true;

        unsubscribeSession = onSessionMessage((msg) => {
            if (msg.type === 'activity') onForeignActivity();
            else if (msg.type === 'logout') onForeignLogout();
        });

        ACTIVITY_EVENTS.forEach(name => {
            window.addEventListener(name, onLocalActivity, { passive: true, capture: true });
        });
        document.addEventListener('visibilitychange', onVisibilityChange);

        if (localStorage.getItem(ACTIVITY_KEY) === null) {
            writeLastActivity();
        }
        scheduleTimers();
    };

    const stop = (): void => {
        if (!started) return;
        started = false;

        warningTimer = clearTimer(warningTimer);
        logoutTimer = clearTimer(logoutTimer);
        stopCountdown();
        if (warningOpen) {
            Swal.close();
            warningOpen = false;
        }

        ACTIVITY_EVENTS.forEach(name => {
            window.removeEventListener(name, onLocalActivity, { capture: true } as EventListenerOptions);
        });
        document.removeEventListener('visibilitychange', onVisibilityChange);

        if (unsubscribeSession) {
            unsubscribeSession();
            unsubscribeSession = null;
        }
    };

    watch(() => authStore.isLoggedIn, (loggedIn) => {
        if (loggedIn) start();
        else stop();
    }, { immediate: true });

    onBeforeUnmount(stop);
}
